/**
 * HUE SCENES (2026-10-23): the Hue app's own scenes on a pad and on a timeline cue. OPBridge S331's ask
 * (OPApps/opbridge-opbuttons.md, "Hue scenes on a pad or a timeline cue"); their operator: "make a scene an action, so
 * an OPButtons pad, a rule or a timeline cue could fire Studio: Stream1".
 *
 * READ. `GET /hue/scenes` lists only the Hue bridges paired to the PC asked (no `?on=` on the app's route), so every
 * connected bridge is asked and the lists merged. A Hue bridge id is unique, so the PC that lists it holds it.
 *
 * FIRE (a pad). The app cannot fire a module action inline: OPBridge runs `{module: "hue", command: "scene", args}`
 * from a named action, a rule or a timeline step. So a pad registers ONE named action per scene (`hueSceneActionId`)
 * on the bridge it fires on, once (`PUT /admin/actions/{id}`: every single PUT rewrites OPBridge's whole config file,
 * so never per press), and a press is `POST /actions/{id}/fire`. A 404 (the action is gone) registers it again and
 * fires once more. OPBridge runs the recall on the PC holding the Hue bridge wherever it is fired.
 *
 * TIMELINE. A cue carrying the same module action (`hueSceneAction`), sent as it is.
 *
 * LIT. A pad is lit while its scene is its room's active one, as OPBridge last read it (OPBridge caches the list
 * 10 s and marks a recall at once): read again just after a press, and every 15 s while a Hue pad is on screen.
 */
import { createSignal } from 'solid-js';
import { bridges, getConnection } from '../opbridge/store.ts';
import { panicLatched } from '../performance/panicLatch.ts';
import { OpbridgeError, type HueScene, type HueSceneGroup, type HueScenesResponse, type HueSwatch, type NamedAction, type RuleAction } from '../opbridge/httpClient.ts';
import type { FireResult } from '../opbridge/dispatch.ts';

/** What a pad or a cue stores: the Hue bridge and scene, plus the names and colours last seen (so the face and the
 *  timeline can draw it with no OPBridge reachable). */
export interface HueSceneRef { bridge: string; scene: string; name?: string; group?: string; swatch?: HueSwatch[] }

export type HueListStatus = 'unknown' | 'loading' | 'ok' | 'unauthorized' | 'unsupported' | 'error';
export interface HueListState { status: HueListStatus; at: number; detail?: string; bridges: HueScenesResponse['bridges'] }
const EMPTY: HueListState = { status: 'unknown', at: 0, bridges: [] };
const [states, setStates] = createSignal<Record<string, HueListState>>({});
export const hueState = (rowId: string): HueListState => states()[rowId] ?? EMPTY;
const record = (rowId: string, st: HueListState) => setStates((prev) => ({ ...prev, [rowId]: st }));

const RETRY_MS = 10_000;
const inFlight = new Set<string>();

/** Read one bridge's scenes now. Never throws. `rowId` is a local bridge row id. */
export async function refreshHueScenes(rowId: string): Promise<void> {
  if (!rowId || inFlight.has(rowId)) return;
  const conn = getConnection(rowId);
  if (!conn || conn.status() !== 'connected') { record(rowId, { ...hueState(rowId), status: 'error', at: Date.now(), detail: 'That computer is not connected.' }); return; }
  inFlight.add(rowId);
  if (hueState(rowId).status === 'unknown') record(rowId, { ...EMPTY, status: 'loading', at: Date.now() });
  try {
    const res = await conn.httpClient().hueScenes();
    record(rowId, { status: 'ok', at: Date.now(), bridges: Array.isArray(res?.bridges) ? res.bridges : [] });
  } catch (err) {
    const code = err instanceof OpbridgeError ? err.httpStatus : undefined;
    if (code === 401 || code === 403) record(rowId, { ...EMPTY, status: 'unauthorized', at: Date.now(), detail: 'OPBridge has not given this device Hue scenes. Re-pair it, or update OPBridge (v1.15.211 or later).' });
    else if (code === 404) record(rowId, { ...EMPTY, status: 'unsupported', at: Date.now(), detail: 'This OPBridge is too old for Hue scenes (needs v1.15.211 or later).' });
    else record(rowId, { ...EMPTY, status: 'error', at: Date.now(), detail: err instanceof Error ? err.message : String(err) });
  } finally {
    inFlight.delete(rowId);
  }
}

/** Read if never read, or if the last read failed over `RETRY_MS` ago. Cheap to call from an effect. */
export function ensureHueScenes(rowId: string): void {
  const st = hueState(rowId);
  if (st.status === 'ok' || st.status === 'loading') return;
  if (st.status !== 'unknown' && Date.now() - st.at < RETRY_MS) return;
  if (getConnection(rowId)?.status() !== 'connected') return;
  void refreshHueScenes(rowId);
}
/** Every connected bridge (the Hue bridge can be paired to any of them). */
export function ensureAllHueScenes(): void {
  for (const b of bridges()) if (getConnection(b.id)?.status() === 'connected') ensureHueScenes(b.id);
}

/** One scene as the pickers list it: where it was read, its Hue bridge, its room or zone, and the scene. */
export interface HueSceneEntry { rowId: string; bridge: string; group: Pick<HueSceneGroup, 'id' | 'name' | 'kind'>; scene: HueScene }

/** Every scene every connected bridge listed, each once (the first PC to list a Hue bridge is its home), rooms in
 *  OPBridge's order (the room with most ticked lights first), scenes by name. */
export function hueSceneEntries(): HueSceneEntry[] {
  const out: HueSceneEntry[] = [];
  const seen = new Set<string>();
  for (const b of bridges()) {
    const st = hueState(b.id);
    if (st.status !== 'ok') continue;
    for (const hb of st.bridges) {
      for (const g of hb.groups ?? []) {
        const scenes = [...(g.scenes ?? [])].sort((x, y) => x.name.localeCompare(y.name, undefined, { numeric: true, sensitivity: 'base' }));
        for (const sc of scenes) {
          const key = `${hb.id}|${sc.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push({ rowId: b.id, bridge: hb.id, group: { id: g.id, name: g.name, kind: g.kind }, scene: sc });
        }
      }
    }
  }
  return out;
}

/** The listed scene a pad or cue names, or null (not read yet, or no longer in the Hue app). */
export function findHueScene(ref: Pick<HueSceneRef, 'bridge' | 'scene'> | undefined): HueSceneEntry | null {
  if (!ref?.bridge || !ref.scene) return null;
  return hueSceneEntries().find((e) => e.bridge === ref.bridge && e.scene.id === ref.scene) ?? null;
}

/** What the pickers say while nothing is listed: reading, none, or why not. */
export function hueListSummary(): { state: 'reading' | 'none' | 'ok' | 'blocked' | 'nobridge'; detail?: string } {
  const rows = bridges().filter((b) => getConnection(b.id)?.status() === 'connected');
  if (rows.length === 0) return { state: 'nobridge', detail: 'No computer is connected.' };
  if (hueSceneEntries().length > 0) return { state: 'ok' };
  const sts = rows.map((b) => hueState(b.id));
  if (sts.some((s) => s.status === 'unknown' || s.status === 'loading')) return { state: 'reading' };
  const why = sts.find((s) => s.status === 'unauthorized' || s.status === 'unsupported') ?? sts.find((s) => s.status === 'error');
  if (why && sts.every((s) => s.status !== 'ok')) return { state: 'blocked', ...(why.detail ? { detail: why.detail } : {}) };
  return { state: 'none', detail: 'No Hue scenes: pair a Hue bridge and tick its lights on OPBridge\'s Lights page.' };
}

/** "Studio: Stream1". */
export function hueSceneLabel(ref: Pick<HueSceneRef, 'name' | 'group' | 'scene'>): string {
  const name = ref.name || ref.scene;
  return ref.group ? `${ref.group}: ${name}` : name;
}

/** What a pad or cue stores for a listed scene. */
export function hueRefOf(e: HueSceneEntry): HueSceneRef {
  return { bridge: e.bridge, scene: e.scene.id, name: e.scene.name, group: e.group.name, ...(e.scene.swatch ? { swatch: e.scene.swatch } : {}) };
}

/** The scene as a rules-engine action: what a timeline cue carries and what the pad's named action runs. */
export function hueSceneAction(ref: Pick<HueSceneRef, 'bridge' | 'scene'>): RuleAction {
  return { module: 'hue', command: 'scene', args: { bridge: ref.bridge, scene: ref.scene } };
}

/** The named action a pad fires for a scene: one per scene (pads naming one scene share it), never a built-in id. */
export function hueSceneActionId(ref: Pick<HueSceneRef, 'bridge' | 'scene'>): string {
  const clean = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '');
  return `opbuttons-hue-${clean(ref.bridge)}-${clean(ref.scene)}`.slice(0, 120);
}

/** A swatch as CSS: one stop per light (the scene's own colours, each at its level), off lights near black. */
export function hueSwatchCss(swatch: HueSwatch[] | undefined): string | undefined {
  const list = (swatch ?? []).slice(0, 24);
  if (list.length === 0) return undefined;
  const stops = list.map((s, i) => {
    const c = s ? s.rgb.map((v) => Math.round(v * (0.35 + 0.65 * Math.min(100, Math.max(0, s.level)) / 100))) : [18, 18, 20];
    const a = (i / list.length) * 100; const b = ((i + 1) / list.length) * 100;
    return `rgb(${c[0]} ${c[1]} ${c[2]}) ${a.toFixed(1)}% ${b.toFixed(1)}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

// ---- the pad's press ----

/** `${rowId}|${actionId}` registered this page load (a 404 at fire time re-registers, so this is only a fast path). */
const registered = new Set<string>();

async function register(rowId: string, ref: HueSceneRef): Promise<void> {
  const conn = getConnection(rowId)!;
  const id = hueSceneActionId(ref);
  const action: NamedAction = { id, label: `Hue: ${hueSceneLabel(ref)}`, payload: { kind: 'dispatch', ...hueSceneAction(ref), consumer: 'opbuttons' } };
  await conn.httpClient().putNamedAction(id, action);
  registered.add(`${rowId}|${id}`);
}

/** Make sure the scene's named action is on `rowId` (the Inspector calls it when a scene is picked, so the first
 *  press is one request). Never throws. */
export async function prepareHueScene(rowId: string, ref: HueSceneRef): Promise<boolean> {
  if (!rowId || !ref.bridge || !ref.scene || getConnection(rowId)?.status() !== 'connected') return false;
  if (registered.has(`${rowId}|${hueSceneActionId(ref)}`)) return true;
  try { await register(rowId, ref); return true; } catch { return false; }
}

function failure(rowId: string, err: unknown): FireResult {
  const code = err instanceof OpbridgeError ? err.httpStatus : undefined;
  if (code === 401 || code === 403) return { target: rowId, kind: 'http-error', detail: 'this device may not recall Hue scenes there: re-pair it, or update OPBridge (v1.15.211 or later)' };
  if (code === 422) {
    const at = err instanceof Error ? err.message.indexOf('{') : -1;
    let why = '';
    try { why = at >= 0 ? String((JSON.parse((err as Error).message.slice(at)) as { error?: unknown }).error ?? '') : ''; } catch { /* plain text */ }
    return { target: rowId, kind: 'http-error', detail: why || 'the Hue bridge did not take it' };
  }
  return { target: rowId, kind: 'http-error', detail: err instanceof Error ? err.message : String(err) };
}

/** Recall `ref` through `rowId`. One request when the action is already there; a 404 registers it and fires again. */
export async function fireHueScene(rowId: string, ref: HueSceneRef): Promise<FireResult[]> {
  if (!ref.bridge || !ref.scene) return [{ target: rowId || '<default>', kind: 'unsupported-step', detail: 'No scene chosen: pick one in the Inspector.' }];
  // PANIC LOCKED blocks every send; a recall goes by its row, past the shared
  // gate in `resolveBridges` (audit 2026-11-03 A-8). Same answer that gate gives.
  if (panicLatched()) return [];
  const conn = rowId ? getConnection(rowId) : null;
  if (!conn) return [{ target: rowId || '<default>', kind: 'bridge-unknown', detail: 'No computer to send it to.' }];
  if (conn.status() !== 'connected') return [{ target: rowId, kind: 'bridge-disconnected' }];
  const id = hueSceneActionId(ref);
  try {
    if (!registered.has(`${rowId}|${id}`)) await register(rowId, ref);
    try {
      await conn.httpClient().fireNamedAction(id);
    } catch (err) {
      if (!(err instanceof OpbridgeError && err.httpStatus === 404)) throw err;
      registered.delete(`${rowId}|${id}`);
      await register(rowId, ref);
      await conn.httpClient().fireNamedAction(id);
    }
  } catch (err) {
    return [failure(rowId, err)];
  }
  // The room's active scene moved: read the list that holds this Hue bridge again (OPBridge marked it at once).
  const home = findHueScene(ref)?.rowId ?? rowId;
  setTimeout(() => void refreshHueScenes(home), 600);
  return [{ target: rowId, kind: 'ok' }];
}

// ---- keeping "lit" honest while a Hue pad is on screen ----

let watchers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
/** Keep the scene lists fresh (every 15 s) while anything showing a scene's state is mounted. Returns the release. */
export function watchHueScenes(): () => void {
  watchers += 1;
  ensureAllHueScenes();
  if (!timer) timer = setInterval(() => { for (const b of bridges()) if (getConnection(b.id)?.status() === 'connected') void refreshHueScenes(b.id); }, 15_000);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    watchers -= 1;
    if (watchers <= 0 && timer) { clearInterval(timer); timer = null; watchers = 0; }
  };
}
